import { useContext, type ReactNode } from 'react';
import { ExternalLinkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Routes } from '@/utils/routes';
import type { OpsOrderDetailFragment } from '@/graphql/fragments/opsOrderDetail.generated';
import {
  formatByline,
  resolutionDestinationLabel,
} from '@/views/PickingJobs/pickingJobLabels';
import { PickingJobStatusBadge } from '@/views/PickingJobs/PickingJobStatusBadge';
import { formatShortDate } from '@/utils/formatter';
import i18n from '@/utils/i18n';
import { ViewerContext } from '@/providers/ViewerContextProvider';
import { SidebarTextField } from './SidebarTextField';
import { canOpenOrderPickingJob } from './pickingJobLinkPolicy';

export type PickingJobLink = NonNullable<OpsOrderDetailFragment['picking_job']>;

interface PickingJobCardProps {
  pickingJob?: PickingJobLink | null;
}

/**
 * Backlink from an ops order to the picking job it is bundled into.
 *
 * Renders nothing when there is no job: an order that never went through
 * colugo picking, or one whose job is still virtual because nothing has been
 * scanned yet. An empty card would suggest the link is broken rather than
 * absent. The link to the job only shows for roles the job page admits.
 */
export function PickingJobCard({ pickingJob }: PickingJobCardProps) {
  const viewer = useContext(ViewerContext);
  if (!pickingJob) {
    return null;
  }
  const canOpenJob = canOpenOrderPickingJob(viewer, pickingJob);

  const { resolution } = pickingJob;
  const pickupDate = pickingJob.carrier_pickup_date
    ? formatShortDate(pickingJob.carrier_pickup_date)
    : '-';

  return (
    <Card>
      <CardContent className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-col tw:gap-3">
          <PickingJobField label={i18n.t('Picking:sidebarStatus')}>
            <PickingJobStatusBadge status={pickingJob.status} />
          </PickingJobField>
          <PickingJobField label={i18n.t('Picking:columnPickupDate')}>
            {pickupDate}
          </PickingJobField>
          <PickingJobField label={i18n.t('Picking:sidebarPicked')}>
            {`${pickingJob.total_amount_picked} / ${pickingJob.total_amount_ordered}`}
          </PickingJobField>
          {/* The resolution is the only explanation for an order canceled or
              delivered with no carrier event behind it, so it belongs on the
              order page rather than one click away on the job. */}
          {resolution && (
            <>
              <PickingJobField
                label={i18n.t('Picking:detailResolutionDestination')}
              >
                {resolutionDestinationLabel(resolution.destination)}
              </PickingJobField>
              <PickingJobField label={i18n.t('Picking:detailResolvedAt')}>
                {formatByline(resolution.resolved_at, resolution.resolved_by)}
              </PickingJobField>
              <SidebarTextField
                label={i18n.t('Picking:detailResolutionReason')}
                value={resolution.reason}
              />
            </>
          )}
        </div>
        {canOpenJob && (
          <>
            <Separator />
            <div className="tw:flex tw:flex-col tw:gap-3">
              <span className="tw:text-sm tw:text-muted-foreground">
                {i18n.t('Picking:sidebarPickingJob')}
              </span>
              <Button
                variant="outline"
                className="tw:justify-start"
                asChild
                title={pickingJob.id}
              >
                <Link to={Routes.pickingJobDetails(pickingJob.id)}>
                  <ExternalLinkIcon className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
                  <span className="tw:truncate">
                    {i18n.t('Picking:sidebarOpenPickingJob')}
                  </span>
                </Link>
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PickingJobField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1">
      <span className="tw:text-sm tw:text-muted-foreground">{label}</span>
      <div className="tw:text-sm">{children}</div>
    </div>
  );
}
